import { useEffect, useState } from 'react';
import { ChevronDown, Sparkles, SquareDashed, Palette, Wand2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectionOpsPanel } from './SelectionOpsPanel';

/** Props the StatusBar's selection chip needs from EditorPage. When
 *  `hasSelection` is false, the modify-selection rows hide
 *  themselves; Quick Mask / Color Range / AI Select Subject stay
 *  reachable because each can CREATE a selection.
 *
 *  Quick Mask toggling lives here (not on a dedicated chip) per the
 *  Tier 1 Group D roadmap entry: "Toggle from status-bar selection
 *  chip popover." */
interface SelectionChipState {
  hasSelection: boolean;
  width: number;
  height: number;
  canGrow: boolean;
  onGrow: (deltaPx: number) => void;
  onInvert: () => void;
  /** AI Select Subject — null hides the entry. */
  onSelectSubject?: (() => void) | null;
  selectSubjectBusy?: boolean;
  /** Quick Mask toggle. The bar reads `quickMaskActive` to flip the
   *  chip label between "Selection…" and "Quick Mask · Done". */
  quickMaskActive: boolean;
  onToggleQuickMask: () => void;
  /** Refine Edge — only meaningful when there's a selection. Hidden
   *  via `hasSelection` gate in the popover. */
  onOpenRefineEdge?: () => void;
  /** Color Range — always available; creates a new selection. */
  onOpenColorRange?: () => void;
}

interface StatusBarProps {
  zoom: number;
  canvasWidth: number;
  canvasHeight: number;
  /** Subscribe-style cursor source. The bar manages its own
   *  cursor state locally; the canvas hook fires the callback
   *  on every `mouse:move` (60+ Hz) and only this component
   *  re-renders. Returns an unsubscribe; lifecycle handled in
   *  a useEffect. */
  subscribeToCursorMove: (cb: (x: number, y: number) => void) => () => void;
  currentColor: string;
  /** Selection chip — null hides the chip entirely (no image
   *  loaded yet). With an image present, the chip is always
   *  visible so Quick Mask and Color Range stay reachable. */
  selection?: SelectionChipState | null;
}

export function StatusBar({
  zoom,
  canvasWidth,
  canvasHeight,
  subscribeToCursorMove,
  currentColor,
  selection,
}: StatusBarProps) {
  const [cursor, setCursor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  useEffect(() => {
    return subscribeToCursorMove((x, y) => setCursor({ x, y }));
  }, [subscribeToCursorMove]);
  return (
    <div className="flex items-center h-7 bg-background border-t border-border px-3 text-xs text-muted-foreground gap-4 select-none">
      <span className="tabular-nums">{Math.round(zoom * 100)}%</span>
      <span className="w-px h-3.5 bg-border" />
      <span className="tabular-nums">
        {canvasWidth} x {canvasHeight}
      </span>
      <span className="w-px h-3.5 bg-border" />
      <span className="tabular-nums">
        X: {cursor.x} Y: {cursor.y}
      </span>
      <span className="w-px h-3.5 bg-border" />
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block w-3.5 h-3.5 rounded border border-border"
          style={{ backgroundColor: currentColor }}
        />
        <span className="tabular-nums uppercase">{currentColor}</span>
      </span>
      {selection && (
        <>
          <span className="w-px h-3.5 bg-border" />
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-foreground hover:bg-accent rounded px-1.5 py-0.5"
                aria-label="Selection menu"
              >
                <span className="tabular-nums">
                  {selection.quickMaskActive
                    ? 'Quick Mask · Done'
                    : selection.hasSelection
                      ? `Selection: ${Math.round(selection.width)}×${Math.round(selection.height)}`
                      : 'Select'}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3 space-y-3" align="end">
              {/* Tools at the top — these CREATE a selection so
                  they make sense regardless of whether one exists.
                  Quick Mask gets a distinct visual treatment when
                  active (primary tint) so the user sees the mode
                  state at a glance. */}
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={selection.onToggleQuickMask}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                    selection.quickMaskActive
                      ? 'bg-primary text-primary-foreground hover:opacity-90'
                      : 'hover:bg-accent text-foreground'
                  }`}
                >
                  <SquareDashed className="h-3.5 w-3.5" />
                  <span>{selection.quickMaskActive ? 'Exit Quick Mask' : 'Quick Mask'}</span>
                  <span className="ml-auto text-[10px] opacity-70">Q</span>
                </button>
                {selection.onOpenColorRange && !selection.quickMaskActive && (
                  <button
                    type="button"
                    onClick={selection.onOpenColorRange}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-foreground"
                  >
                    <Palette className="h-3.5 w-3.5" />
                    <span>Color Range…</span>
                  </button>
                )}
                {selection.onSelectSubject && !selection.quickMaskActive && (
                  <button
                    type="button"
                    onClick={selection.onSelectSubject}
                    disabled={selection.selectSubjectBusy}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>
                      {selection.selectSubjectBusy ? 'Selecting subject…' : 'AI Select Subject'}
                    </span>
                  </button>
                )}
              </div>

              {/* Modify section — only meaningful with a selection.
                  Refine Edge sits here too because it refines an
                  existing selection. */}
              {selection.hasSelection && !selection.quickMaskActive && (
                <div className="space-y-2 pt-2 border-t border-border">
                  <SelectionOpsPanel
                    hasSelection
                    canGrow={selection.canGrow}
                    onGrow={selection.onGrow}
                    onInvert={selection.onInvert}
                    compact
                  />
                  {selection.onOpenRefineEdge && (
                    <button
                      type="button"
                      onClick={selection.onOpenRefineEdge}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-foreground border border-border"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      <span>Refine Edge…</span>
                    </button>
                  )}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
}
